import React, { useState, useRef } from 'react';
import { 
  Card, 
  Text, 
  Button, 
  Group, 
  Stack, 
  Badge,
  Paper,
  Box,
  Grid
} from '@mantine/core';
import { 
  ArrowRight,
  Star,
  Zap,
  Target
} from 'lucide-react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter
} from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { 
  SortableContext,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './FeaturePreviewDemo.css';

interface Task {
  id: string;
  title: string;
  color: string;
  emoji: string;
}

interface ConfettiPiece {
  id: number;
  x: number;
  y: number;
  rotation: number;
  color: string;
  emoji: string;
}

// Confetti hook for magical effects
const useConfetti = () => {
  const [confetti, setConfetti] = useState<ConfettiPiece[]>([]);
  const confettiRef = useRef<number>(0);

  const triggerConfetti = (x: number = 50, y: number = 50) => {
    const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA726', '#AB47BC'];
    const emojis = ['✨', '🎉', '⭐', '💫', '🌟', '🎊'];
    
    const newConfetti: ConfettiPiece[] = [];
    for (let i = 0; i < 15; i++) {
      newConfetti.push({
        id: confettiRef.current++,
        x: x + (Math.random() - 0.5) * 40,
        y: y + (Math.random() - 0.5) * 40,
        rotation: Math.random() * 360,
        color: colors[Math.floor(Math.random() * colors.length)],
        emoji: emojis[Math.floor(Math.random() * emojis.length)]
      });
    }
    
    setConfetti(prev => [...prev, ...newConfetti]);
    
    // Clean up confetti after animation
    setTimeout(() => {
      setConfetti(prev => prev.filter(c => !newConfetti.includes(c)));
    }, 2000);
  };

  return { confetti, triggerConfetti };
};

// Sortable task chip for calendar (enables within-day reordering)
const SortableTask: React.FC<{ 
  task: Task;
  containerId: string; // day id like '15'
}> = ({ task, containerId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: task.id,
    data: { type: 'task', task, containerId }
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
    cursor: 'grab',
  };

  return (
    <Badge
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      size="sm"
      variant="light"
      color="blue"
      leftSection={<span>{task.emoji}</span>}
      className="demo-task-compact"
    >
      {task.title}
    </Badge>
  );
};

// Droppable calendar cell
const CalendarCell: React.FC<{
  date: number;
  dayName?: string;
  children: React.ReactNode;
  isToday?: boolean;
  isOver?: boolean;
}> = ({ date, dayName, children, isToday, isOver }) => {
  const { setNodeRef } = useDroppable({ id: `day-${date}`, data: { type: 'day', containerId: String(date) } });

  return (
    <Paper
      id={`day-${date}`}
      ref={setNodeRef}
      p="xs"
      withBorder
      className={`demo-calendar-cell ${isOver ? 'demo-calendar-over' : ''}`}
      style={{
        minHeight: '100px',
        backgroundColor: isToday 
          ? 'var(--mantine-color-blue-0)' 
          : isOver 
          ? 'var(--mantine-color-green-0)' 
          : 'var(--app-surface)',
        borderColor: isToday ? 'var(--mantine-color-blue-4)' : 'var(--app-border)',
        borderWidth: isToday ? '2px' : '1px',
        transition: 'all 0.3s ease',
        transform: isOver ? 'scale(1.05)' : 'scale(1)',
      }}
    >
      {dayName && (
        <Text 
          size="xs" 
          fw={500}
          mb={2}
          style={{ 
            color: 'var(--app-text-secondary)',
          }}
        >
          {dayName}
        </Text>
      )}
      <Text 
        size="sm" 
        fw={isToday ? 600 : 400}
        mb={4}
        style={{ 
          color: isToday ? 'var(--mantine-color-blue-6)' : 'var(--app-text-primary)',
        }}
      >
        {date}
      </Text>
      <Stack gap={2}>
        {children}
      </Stack>
    </Paper>
  );
};

export const CalendarPreviewDemo: React.FC<{
  onUpgradeClick?: () => void;
}> = ({ onUpgradeClick }) => {
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [moveCount, setMoveCount] = useState(0);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const { confetti, triggerConfetti } = useConfetti();

  const [calendarTasks, setCalendarTasks] = useState<Record<string, Task[]>>({
    '15': [ // Monday
      { id: 'c1', title: 'Team standup', color: '#FF6B6B', emoji: '🚀' },
      { id: 'c4', title: 'Code review', color: '#9C88FF', emoji: '🔍' }
    ],
    '16': [ // Tuesday
      { id: 'c2', title: 'Dentist visit', color: '#4ECDC4', emoji: '🦷' },
      { id: 'c5', title: 'Coffee chat', color: '#FFB800', emoji: '☕' },
      { id: 'c6', title: 'Yoga class', color: '#45B7D1', emoji: '🧘‍♀️' }
    ],
    '17': [ // Wednesday
      { id: 'c3', title: 'Movie night', color: '#FFB800', emoji: '🎬' },
      { id: 'c7', title: 'Reading time', color: '#FF9F43', emoji: '📖' }
    ],
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as any;
    setDraggedTask((data?.task as Task) || (data as Task) || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedTask(null);

    if (!over || !active) return;

    const activeData = active.data.current as any;
    const activeTask: Task | undefined = activeData?.task || activeData;
    if (!activeTask) return;

    // Increment move counter and maybe show upgrade
    setMoveCount(prev => {
      const newCount = prev + 1;
      if (newCount >= 3) {
        setShowUpgradePrompt(true);
        setTimeout(() => setShowUpgradePrompt(false), 4000);
        
        // Auto-scroll to show upgrade button on short screens
        setTimeout(() => {
          const upgradePrompt = document.querySelector('.upgrade-prompt');
          if (upgradePrompt) {
            upgradePrompt.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 100); // Small delay to ensure DOM is updated
      }
      return newCount;
    });

    // Confetti at drop
    const targetId = String(over.id);
    const rect = document.getElementById(targetId)?.getBoundingClientRect();
    if (rect) {
      const x = ((rect.left + rect.width / 2) / window.innerWidth) * 100;
      const y = ((rect.top + rect.height / 2) / window.innerHeight) * 100;
      triggerConfetti(x, y);
    } else {
      triggerConfetti();
    }

    const sourceDay: string | undefined = activeData?.containerId ||
      Object.keys(calendarTasks).find(day => calendarTasks[day].some(t => t.id === activeTask.id));
    if (!sourceDay) return;

    // Determine target container and index
    const overData = over.data.current as any;
    let targetDay: string | undefined;
    let targetIndex: number | undefined;

    if (overData?.type === 'task') {
      targetDay = overData.containerId as string | undefined;
      const list: Task[] = targetDay ? (calendarTasks[targetDay] || []) : [];
      const overIdx = list.findIndex((t: Task) => t.id === over.id);
      targetIndex = overIdx >= 0 ? overIdx : list.length;
    } else if (targetId.startsWith('day-')) {
      targetDay = targetId.replace('day-', '');
      targetIndex = (calendarTasks[targetDay] || []).length;
    }

    if (!targetDay) return;

    setCalendarTasks(prev => {
      const next = { ...prev } as Record<string, Task[]>;
      const fromList = [...(next[sourceDay] || [])];
      const fromIndex = fromList.findIndex(t => t.id === activeTask.id);
      if (fromIndex === -1) return prev;
      const [moved] = fromList.splice(fromIndex, 1);

      if (sourceDay === targetDay) {
        // Reorder within same day
        const reordered = arrayMove([...(next[targetDay] || [])], fromIndex, Math.min(targetIndex!, (next[targetDay] || []).length));
        next[targetDay] = reordered;
      } else {
        // Move across days
        const toList = [...(next[targetDay] || [])];
        const insertAt = Math.min(targetIndex ?? toList.length, toList.length);
        toList.splice(insertAt, 0, moved);
        next[sourceDay] = fromList;
        next[targetDay] = toList;
      }
      return next;
    });
  };

  const renderCalendarDemo = () => {
    // Show 3 days for better mobile experience
    const days = [
      { date: 15, name: 'Monday' },
      { date: 16, name: 'Tuesday' },
      { date: 17, name: 'Wednesday' }
    ];
    
    return (
      <Grid gutter="md">
        {days.map(({ date, name }) => (
          <Grid.Col key={date} span={{ base: 12, sm: 4 }}>
            <CalendarCell 
              date={date}
              dayName={name}
              isToday={date === 16} // Tuesday is "today"
            >
              <SortableContext items={(calendarTasks[date.toString()] || []).map(t => t.id)}>
                {calendarTasks[date.toString()]?.map(task => (
                  <SortableTask key={task.id} task={task} containerId={String(date)} />
                ))}
              </SortableContext>
            </CalendarCell>
          </Grid.Col>
        ))}
      </Grid>
    );
  };

  return (
    <div className="feature-preview-demo">
      {/* Confetti overlay */}
      <div className="confetti-container">
        {confetti.map(piece => (
          <div
            key={piece.id}
            className="confetti-piece"
            style={{
              left: `${piece.x}%`,
              top: `${piece.y}%`,
              transform: `rotate(${piece.rotation}deg)`,
              color: piece.color,
            }}
          >
            {piece.emoji}
          </div>
        ))}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Card 
          withBorder 
          p="xl" 
          className="demo-card"
          style={{ maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden', paddingBottom: 32 }}
        >
          <Stack gap="lg">
            {/* Move counter */}
            <Group justify="flex-end">
              <Badge 
                variant="light" 
                color={moveCount >= 3 ? 'orange' : 'blue'}
                leftSection={<Target size={12} />}
              >
                {moveCount}/3 moves
              </Badge>
            </Group>

            {/* Calendar Demo */}
            <Box>
              <Text size="sm" style={{ color: 'var(--app-text-secondary)', marginBottom: '12px' }}>
                📅✨ This week: Drag tasks between days to reschedule!
              </Text>
              <Box style={{ minHeight: '120px' }}>
                {renderCalendarDemo()}
              </Box>
            </Box>

            {/* Upgrade prompt */}
            {(moveCount >= 3 || showUpgradePrompt) && (
              <Paper 
                p="md" 
                withBorder 
                className="upgrade-prompt"
                style={{
                  background: 'linear-gradient(135deg, var(--app-surface) 0%, var(--app-hover) 100%)',
                  borderColor: 'var(--mantine-color-blue-4)',
                  borderWidth: '2px',
                  boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
                }}
              >
                <Group justify="space-between" align="center">
                  <Group gap="sm">
                    <Zap size={20} style={{ color: 'var(--mantine-color-orange-5)' }} />
                    <div>
                      <Text fw={600} size="sm" style={{ color: 'var(--app-text-primary)' }}>
                        🎉 You're a natural planner!
                      </Text>
                      <Text size="xs" style={{ color: 'var(--app-text-secondary)' }}>
                        Upgrade for month/week views, time scheduling, and advanced planning
                      </Text>
                    </div>
                  </Group>
                  
                  <Button 
                    size="sm"
                    variant="gradient"
                    gradient={{ from: 'blue', to: 'purple', deg: 45 }}
                    leftSection={<Star size={14} />}
                    rightSection={<ArrowRight size={14} />}
                    onClick={onUpgradeClick}
                  >
                    Upgrade Now
                  </Button>
                </Group>
              </Paper>
            )}

            {/* Feature highlights */}
            <Paper p="md" withBorder className="features-highlight">
              <Text fw={600} size="sm" mb="xs" style={{ color: 'var(--app-text-primary)' }}>
                ✨ Premium Calendar Features Include:
              </Text>
              <Group gap="lg">
                <Text size="xs" style={{ color: 'var(--app-text-secondary)' }}>
                  • Month & week views
                </Text>
                <Text size="xs" style={{ color: 'var(--app-text-secondary)' }}>
                  • Time-based scheduling
                </Text>
                <Text size="xs" style={{ color: 'var(--app-text-secondary)' }}>
                  • Deadline management
                </Text>
                <Text size="xs" style={{ color: 'var(--app-text-secondary)' }}>
                  • Drag & drop planning
                </Text>
              </Group>
            </Paper>

            {/* Bottom spacer to ensure final content can scroll fully into view */}
            <div style={{ height: 48 }} />
          </Stack>
        </Card>

        {/* Drag overlay */}
        <DragOverlay>
          {draggedTask && (
            <Badge 
              size="sm" 
              variant="filled" 
              color="blue" 
              className="demo-task-compact"
              style={{
                transform: 'rotate(5deg)',
                opacity: 0.95,
                boxShadow: '0 15px 35px rgba(0, 0, 0, 0.3)',
                zIndex: 1000
              }}
            >
              <span style={{ marginRight: 6 }}>{draggedTask.emoji}</span>
              {draggedTask.title}
            </Badge>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};